/* eslint-disable react/no-array-index-key */
import React, { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import styled, { StyleSheetManager, ThemeProvider } from 'styled-components';
import { useLDClient, withLDProvider } from 'launchdarkly-react-client-sdk';
import { createHistory, createMemorySource, LocationProvider } from '@reach/router';
import { walleyLightTheme, walleyDarkTheme } from '../theme/themes';
import WalleyGlobalStyles from '../theme/globalStyles';
import routes from '../routes';
import InviteLandingPage from './InviteLandingPage/InviteLandingPage';
import MerchantSignupPage, { isMerchantSignupPage } from './MerchantSignup/MerchantSignupPage';
import { config, ExtendedWindow } from '../config';
import UserNotFound from './UserNotFound';
import { useAppState } from '../store/appstate';
import { checkForNewVersion } from '../services/helpers/checkForNewVersion';
import Button from '../components/atoms/Button/Button';
import {
    setAlertMessage,
    setNewVersionAvailable,
    setThemeMode,
    setUserFlow,
    UserSettingsState,
} from '../store/userSettings/userSettingsSlice';
import {
    SessionState,
    showProcessingNotComplete,
    userAuthenticated,
} from '../store/session/sessionSlice';
import NavBar from '../components/molecules/Navbar/Navbar';
import RefreshMessage from '../components/atoms/Message/RefreshMessage';
import AlertBanner from '../components/atoms/Message/AlertBanner';
import { LockedModal } from '../components/atoms/Modal/LockedModal';
import LoadingScreen from './LoadingScreen';
import { useAppDispatch } from '../store';
import UserWithoutPermission from './UserWithoutPermission';
import Survey from '../components/molecules/Ces/Survey';
import surveyActivatedOn from '../services/surveyActivatedOn.json';
import isPropValid from '@emotion/is-prop-valid';
import sessionActions from '../store/session/actions';
import { useAccount, useMsal, useMsalAuthentication } from '@azure/msal-react';
import {
    InteractionRequiredAuthError,
    InteractionType,
    AuthError,
    ServerError,
} from '@azure/msal-browser';
import { match } from 'ts-pattern';
import Logger from '../services/logging/logger';
import { useIsDocumentVisible } from '../services/hooks/useIsDocumentVisible';
import {
    useIsGuidedSetupEnabled,
    useIsGuidedSetupLoaded,
} from '../services/hooks/useIsGuidedSetupEnabled';

// This implements the default behavior from styled-components v5
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function shouldForwardProp(propName: string, target: any): boolean {
    if (typeof target === 'string') {
        // For HTML elements, forward the prop if it is a valid HTML attribute
        return isPropValid(propName);
    }
    // For other elements, forward all props
    return true;
}

declare let window: ExtendedWindow;
const App: React.FC = () => {
    const dispatch = useAppDispatch();
    const location = useLocation();

    const {
        newVersionAvailable: updateAvailable = false,
        flow,
        alertMessage,
        themeMode,
    } = useAppState<UserSettingsState>(s => s.userSettings);

    useEffect(() => {
        const savedTheme = localStorage.getItem('themeMode');
        if (!savedTheme && window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
            Logger.information('Auto-detected user prefers dark mode from OS settings');
            dispatch(setThemeMode('dark'));
        }
    }, [dispatch]);

    const { hostname } = window.location;

    const domainFlow = config.Auth.Flows ? config.Auth.Flows[hostname] : undefined;
    const defaultFlow = domainFlow || config.Auth.DefaultFlow;
    const getFlowFromQueryString = () => new URLSearchParams(location.search).get('flow');
    const getAuthority = (whichFlow?: string) =>
        config.Auth.Authority + (whichFlow || getFlowFromQueryString() || defaultFlow);
    const { user: { isInternal } = { isInternal: false }, failedFetchingUser } =
        useAppState<SessionState>(s => s.session);

    const authority = getAuthority(flow);
    const request = {
        scopes: ['openid', 'profile'],
        extraScopesToConsent: config.Auth.ApiScopes,
        state: location.pathname,
        authority,
    };
    const { login, error } = useMsalAuthentication(InteractionType.Silent, request);
    const { accounts, instance } = useMsal();

    const account = useAccount(accounts[0] || {});
    const accountsExist = accounts.length > 0;

    const handleAcquireTokenError = (e: unknown) => {
        if (e instanceof ServerError && e.message.indexOf('AADB2C90090') > -1) {
            Logger.warning('Server error AADB2C90090 encountered, restarting login flow', e);
            instance.clearCache();
            instance.logout();
            login(InteractionType.Redirect, request);
            return;
        }

        if (!(e instanceof AuthError)) throw e;
        const err = e as AuthError;
        Logger.warning('Error when aqcuring token :', err);

        if (window.location.pathname.startsWith('/invitation/') || isMerchantSignupPage()) {
            return;
        }

        if (err && err.message.indexOf('AADB2C90118') > -1) {
            Logger.warning('Password reset required, redirecting to password reset flow');
            login(InteractionType.Redirect, {
                ...request,
                authority: getAuthority(config.Auth.PasswordResetFlow),
            });

            return;
        }

        if (account && account.tenantId == config.Auth.PasswordResetFlow) {
            instance.clearCache();
            instance.logout();
            login(InteractionType.Redirect, request);
        }

        if (
            err &&
            (err.message.indexOf('AADB2C90077') > -1 ||
                err.message.indexOf('AADB2C90157') > -1 ||
                err.message.indexOf('monitor_window_timeout') > -1)
        ) {
            login(InteractionType.Redirect, request);

            return;
        }

        if (
            err &&
            (err.message.indexOf('AADB2C90088') > -1 || err.message.indexOf('AADB2C90091') > -1)
        ) {
            instance.clearCache();
            instance.logout();
            login(InteractionType.Redirect, request);

            return;
        }

        if (err instanceof InteractionRequiredAuthError) {
            if (!localStorage.getItem('PATH_BEFORE_AUTH')) {
                localStorage.setItem('PATH_BEFORE_AUTH', location.pathname + location.search);
            }
            login(InteractionType.Redirect, request);
        }
    };

    const refreshToken = () => {
        if (account) {
            instance
                .acquireTokenSilent({
                    scopes: config.Auth.ApiScopes,
                    authority,
                })
                .then(response => {
                    if (response) {
                        dispatch(
                            userAuthenticated({
                                token: response.accessToken,
                                expiryTime: response.expiresOn?.getTime() || 0,
                            })
                        );
                    }
                })
                .catch(handleAcquireTokenError);
        }
    };

    useEffect(() => {
        if (error) handleAcquireTokenError(error);
    }, [error]);

    const { user, expiryTime, nextRefresh, showProcessingNotCompletedMessage } =
        useAppState<SessionState>(s => s.session);

    useEffect(() => {
        if (account) {
            instance.initialize().then(() => {
                instance
                    .acquireTokenSilent({
                        scopes: config.Auth.ApiScopes,
                        account,
                        authority,
                    })
                    .then(response => {
                        if (response) {
                            dispatch(
                                sessionActions.saveUserSession(
                                    response.accessToken,
                                    response.expiresOn?.getTime() || 0,
                                    Date.now()
                                )
                            );
                            dispatch(setUserFlow(getFlowFromQueryString() ?? ''));
                        }
                    })
                    .catch(handleAcquireTokenError);
            });
            instance.setActiveAccount(account);
        }
    }, [account]);

    const ldClient = useLDClient();
    const alertBannerMessage = ldClient?.variation('Permanent_MH_Frontend_Alert_Message', '');

    useEffect(() => {
        if (alertBannerMessage && alertBannerMessage.trim() !== '') {
            dispatch(setAlertMessage(alertBannerMessage));
        } else {
            dispatch(setAlertMessage(undefined));
        }
    }, [alertBannerMessage, dispatch]);

    useEffect(() => {
        const timeUntilExpiry = expiryTime ? expiryTime - Date.now() : 0;
        const timeoutId = setTimeout(refreshToken, timeUntilExpiry);

        return () => clearTimeout(timeoutId);
    }, [account, expiryTime]);

    // Organizations in the guided setup land on it instead of Orders.
    const isGuidedSetupEnabled = useIsGuidedSetupEnabled();
    const isGuidedSetupLoaded = useIsGuidedSetupLoaded();

    const renderRedirect = () => {
        // The landing page depends on the organization, so wait until it is loaded.
        if (!isGuidedSetupLoaded) return <Route path="*" element={<LoadingScreen />} />;

        const redirect = localStorage.getItem('PATH_BEFORE_AUTH');
        if (redirect) localStorage.removeItem('PATH_BEFORE_AUTH');

        const redirectPath = redirect || (isGuidedSetupEnabled ? '/get-started' : '/orders');
        const path = user && Object.keys(user).length > 0 ? redirectPath : '/usernotfound';

        return <Route path="*" element={<Navigate to={path} replace />} />;
    };

    const intervalTime = config.Environment !== 'PROD' ? 60000 : 300000;

    useEffect(() => {
        let setNewVersionAvailableTimeout: ReturnType<typeof setTimeout>;

        const showUpdateAvailable = () => {
            Logger.information("Displaying 'New version available' message");
            setNewVersionAvailableTimeout = setTimeout(() => {
                dispatch(setNewVersionAvailable(true));
            }, intervalTime);
        };

        const runCheckForNewVersion = setInterval(
            () => checkForNewVersion(showUpdateAvailable),
            intervalTime
        );

        return () => {
            if (setNewVersionAvailableTimeout) {
                clearTimeout(setNewVersionAvailableTimeout);
            }

            clearInterval(runCheckForNewVersion);
        };
    });

    const isDocumentVisible = useIsDocumentVisible();

    useEffect(() => {
        if (isDocumentVisible) {
            const timeUntilExpiry = expiryTime ? expiryTime - Date.now() : 0;
            if (timeUntilExpiry <= 0) refreshToken();
        }
    }, [isDocumentVisible, expiryTime]);

    useEffect(() => {
        if (accountsExist && user && user.organization && Date.now() < expiryTime) {
            if (nextRefresh && Date.now() > nextRefresh) {
                dispatch(sessionActions.refreshUser(Date.now()));
            }
        }
    }, [accountsExist, user, user?.organization, expiryTime, nextRefresh, dispatch]);

    const refresh = () => {
        dispatch(showProcessingNotComplete(false));
        window.location.reload();
    };

    const theme = themeMode === 'dark' ? walleyDarkTheme : walleyLightTheme;
    const source = createMemorySource('*');
    const history = createHistory(source ?? window);
    const userWithoutPermission = user?.permissions?.length === 0;
    const invitation = window.location.pathname.startsWith('/invitation/');
    const [, invitationId] = invitation ? window.location.pathname.split('/invitation/') : '';
    // The merchant signup page is public: it is shown before (and without) any sign-in, like the invitation landing
    // page, in the environments where the config enables it.
    const merchantSignup = isMerchantSignupPage();

    return (
        <>
            <ThemeProvider theme={theme}>
                <StyleSheetManager shouldForwardProp={shouldForwardProp}>
                    <WalleyGlobalStyles theme={theme} />
                    {match({
                        failedFetchingUser,
                        userWithoutPermission,
                        invitation,
                        merchantSignup,
                    })
                        .with({ merchantSignup: true }, () => <MerchantSignupPage />)
                        .with({ invitation: true }, () => (
                            <InviteLandingPage invitationId={invitationId} />
                        ))
                        .with({ failedFetchingUser: true }, () => <UserNotFound />)
                        .with({ userWithoutPermission: true }, () => <UserWithoutPermission />)
                        .otherwise(() => (
                            <LocationProvider history={history}>
                                {accountsExist && (
                                    <>
                                        {updateAvailable && <RefreshMessage />}
                                        {alertMessage && (
                                            <AlertBanner
                                                severity="warning"
                                                message={alertMessage}
                                                onClose={() => dispatch(setAlertMessage(undefined))}
                                            />
                                        )}
                                    </>
                                )}
                                <Routes>
                                    {user &&
                                        user.permissions &&
                                        user?.permissions.length > 0 &&
                                        accountsExist && (
                                            <>
                                                {routes.map((r, key) => {
                                                    return (
                                                        <Route
                                                            key={key}
                                                            path={r.path}
                                                            element={
                                                                <div>
                                                                    <NavBar user={user} />

                                                                    <Container>
                                                                        {
                                                                            r.component as React.ReactNode
                                                                        }
                                                                    </Container>
                                                                </div>
                                                            }
                                                        />
                                                    );
                                                })}
                                                {renderRedirect()}
                                            </>
                                        )}

                                    <Route path="*" element={<LoadingScreen />} />
                                </Routes>

                                {user &&
                                    user.permissions &&
                                    user?.permissions.length > 0 &&
                                    accountsExist && (
                                        <>
                                            <LockedModal
                                                small
                                                isShown={showProcessingNotCompletedMessage}
                                                hide={() =>
                                                    dispatch(showProcessingNotComplete(false))
                                                }
                                            >
                                                <ProcessContainer>
                                                    Order has been updated and will show the correct
                                                    data on refresh.
                                                    <StyledButton large onClick={() => refresh()}>
                                                        Refresh
                                                    </StyledButton>
                                                </ProcessContainer>
                                            </LockedModal>

                                            {!isInternal && (
                                                <Survey
                                                    surveyActivatedOn={
                                                        surveyActivatedOn.surveyActivatedOn
                                                    }
                                                />
                                            )}
                                        </>
                                    )}
                            </LocationProvider>
                        ))}
                </StyleSheetManager>
            </ThemeProvider>
        </>
    );
};

const launchDarklyClientId = import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID;

export default withLDProvider({
    clientSideID: config.LaunchDarklyApiKey || launchDarklyClientId || '',
    context: {
        key: 'anonymous-users-shared-key',
    },
})(App);

const Container = styled.div`
    margin: 0 auto;
`;

const ProcessContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin: 3rem;
    gap: 3rem;
`;

const StyledButton = styled(Button)`
    width: 10rem;
    padding: 1rem;
    align-self: flex-end;
`;
